import type { CSSProperties } from 'react';
import { useDuration } from './shared/useDuration';
import { CachedImage } from './shared/CachedImage';
import { BrandLogo } from './shared/BrandLogo';
import { brandTokens, fontFamilyFor } from './shared/tokens';
import type { ManifestMenuBoardItem, MediaRef } from '../types';

interface MenuGridProps {
  item: ManifestMenuBoardItem;
  onEnd: () => void;
}

// menu-grid — image-forward menu board for fast food, coffee, shawarma,
// chicken, desserts. Each section renders as a labeled grid of cards
// (auto-fit with min width 18rem → 4 cards/row on 1920p, 3 on 1366p,
// 2 on portrait).
//
// Cards have a 4:3 image area on top OR a brand-tinted placeholder when
// no image is present (gracefully degrades the "image-forward" promise).
// Body below the image: name (bold) + optional description (lighter) +
// large accent-colored price.
export function MenuGrid({ item, onEnd }: MenuGridProps) {
  useDuration(item.durationMs, onEnd);

  const { brand, menu } = item;
  const accent = menu.accentColor ?? brand.primaryColor ?? '#c89a3d';
  const accentFontStack = fontFamilyFor(brand.accentFont);

  return (
    <div style={{ ...rootStyle, ...brandTokens(brand) }}>
      {menu.background ? (
        <>
          <CachedImage mediaRef={menu.background} style={bgImageStyle} />
          <div style={bgOverlayStyle} />
        </>
      ) : null}

      <div style={contentStyle}>
        <header style={headerStyle}>
          <h1
            style={{
              ...titleStyle,
              fontFamily: accentFontStack,
            }}
          >
            {menu.name}
          </h1>
          <div style={{ ...accentBarStyle, background: accent }} />
        </header>

        <div style={sectionsStreamStyle}>
          {menu.sections.map((section) => (
            <section key={section.sectionId} style={sectionStyle}>
              <h2
                style={{
                  ...sectionTitleStyle,
                  color: accent,
                  fontFamily: accentFontStack,
                }}
              >
                {section.name}
              </h2>

              <div style={gridStyle}>
                {section.items.map((it) => (
                  <ItemCard
                    key={it.itemId}
                    image={it.image}
                    name={it.name}
                    description={it.description}
                    priceCents={it.priceCents}
                    badges={it.badges}
                    accent={accent}
                    currency={brand.currency}
                    accentFontStack={accentFontStack}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      <BrandLogo brand={brand} position="top-right" maxHeight="6vh" />
    </div>
  );
}

interface ItemCardProps {
  image: MediaRef | null;
  name: string;
  description: string | null;
  priceCents: number;
  badges: string[];
  accent: string;
  currency: string;
  accentFontStack: string;
}

function ItemCard({
  image,
  name,
  description,
  priceCents,
  badges,
  accent,
  currency,
  accentFontStack,
}: ItemCardProps) {
  return (
    <div style={cardStyle}>
      <div style={cardImageContainerStyle}>
        {image ? (
          <CachedImage
            mediaRef={image}
            style={cardImageStyle}
            fallback={<NoImagePlaceholder accent={accent} />}
          />
        ) : (
          <NoImagePlaceholder accent={accent} />
        )}
        {badges.length > 0 && (
          <div style={cardBadgesOverlayStyle}>
            {badges.map((b, i) => (
              <span
                key={i}
                style={{
                  ...cardBadgeStyle,
                  background: accent,
                }}
              >
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      <div style={cardBodyStyle}>
        <div style={cardNameStyle}>{name}</div>
        {description ? (
          <div style={cardDescStyle}>{description}</div>
        ) : null}
        <div
          style={{
            ...cardPriceStyle,
            color: accent,
            fontFamily: accentFontStack,
          }}
        >
          {formatPrice(priceCents, currency)}
        </div>
      </div>
    </div>
  );
}

function NoImagePlaceholder({ accent }: { accent: string }) {
  return (
    <div
      style={{
        ...placeholderStyle,
        background: `linear-gradient(135deg, ${accent}26 0%, ${accent}0a 100%)`,
      }}
    >
      <div style={{ ...placeholderIconStyle, color: accent }}>★</div>
    </div>
  );
}

function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

// ---------- Styles ----------

const rootStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  background: '#141414',
  color: '#ffffff',
  overflow: 'hidden',
  fontFamily: '"Inter", system-ui, sans-serif',
};

const bgImageStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0,
};

const bgOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.85) 100%)',
  zIndex: 1,
};

const contentStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '3.5vh 5vw 2.5vh',
  boxSizing: 'border-box',
};

const headerStyle: CSSProperties = {
  marginBottom: '2.5vh',
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(1.8rem, 3.8vw, 3.4rem)',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  lineHeight: 1.1,
  color: '#ffffff',
};

const accentBarStyle: CSSProperties = {
  height: '3px',
  width: 'clamp(6rem, 9vw, 12rem)',
  marginTop: '1.2vh',
  borderRadius: '2px',
};

const sectionsStreamStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '3.5vh',
  overflow: 'hidden',
};

const sectionStyle: CSSProperties = {
  minWidth: 0,
};

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 1.5vh',
  fontSize: 'clamp(1.2rem, 1.9vw, 1.9rem)',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 18rem), 1fr))',
  gap: '2vh 1.4vw',
};

const cardStyle: CSSProperties = {
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.10)',
  borderRadius: '14px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow:
    '0 4px 18px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
};

const cardImageContainerStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  aspectRatio: '4 / 3',
  background: 'rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
};

const cardImageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const placeholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const placeholderIconStyle: CSSProperties = {
  fontSize: 'clamp(2rem, 4vw, 4rem)',
  opacity: 0.45,
  lineHeight: 1,
};

const cardBadgesOverlayStyle: CSSProperties = {
  position: 'absolute',
  top: '0.6em',
  left: '0.6em',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35em',
  alignItems: 'flex-start',
};

const cardBadgeStyle: CSSProperties = {
  fontSize: 'clamp(0.65rem, 0.85vw, 0.85rem)',
  padding: '0.2em 0.7em',
  borderRadius: '999px',
  color: '#000',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: 1.4,
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.35)',
};

const cardBodyStyle: CSSProperties = {
  padding: '1.1vh 1vw 1.4vh',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4vh',
};

const cardNameStyle: CSSProperties = {
  fontSize: 'clamp(0.95rem, 1.35vw, 1.35rem)',
  fontWeight: 600,
  lineHeight: 1.25,
  color: '#ffffff',
};

const cardDescStyle: CSSProperties = {
  fontSize: 'clamp(0.75rem, 0.95vw, 0.95rem)',
  fontWeight: 300,
  lineHeight: 1.35,
  opacity: 0.65,
  marginTop: '0.2vh',
};

const cardPriceStyle: CSSProperties = {
  fontSize: 'clamp(1.1rem, 1.8vw, 1.9rem)',
  fontWeight: 700,
  marginTop: '0.5vh',
  letterSpacing: '-0.01em',
};
